import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../data/site";
import { usePreRegister } from "./PreRegisterModal";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openPreRegister } = usePreRegister();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-brand-obsidian/90 shadow-2xl backdrop-blur-xl"
          : "bg-gradient-to-b from-brand-obsidian/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-8xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" aria-label="OFF360 — Página inicial" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegação principal">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "inicio" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-[13px] font-semibold transition-colors ${
                  isActive
                    ? "text-brand-orange"
                    : "text-slate-200 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="cta-download-app-button"
            onClick={() => openPreRegister({ origem: "header" })}
            className="hidden rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-brand-orange/25 transition-all hover:-translate-y-0.5 hover:bg-brand-orangedark hover:shadow-brand-orange/40 sm:inline-flex"
          >
            Baixar aplicativo
          </button>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 lg:hidden"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-t border-white/10 bg-brand-obsidian/98 backdrop-blur-xl lg:hidden"
            aria-label="Menu móvel"
          >
            <ul className="space-y-1 px-4 py-6">
              {NAV_LINKS.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "inicio" : l.to.slice(1)}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 text-base font-semibold ${
                        isActive ? "bg-white/10 text-brand-orange" : "text-slate-100 hover:bg-white/10"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.li>
              ))}
              <li className="pt-3">
                <button
                  type="button"
                  data-testid="mobile-cta-download-button"
                  onClick={() => {
                    setOpen(false);
                    openPreRegister({ origem: "header-mobile" });
                  }}
                  className="w-full rounded-full bg-brand-orange px-5 py-4 text-base font-bold text-white"
                >
                  Baixar aplicativo
                </button>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
